/**
 * As 3 telas da "Atualização do Instrumento": Lista, Revisão (avaliador) e
 * Conferência (presidência) + modais (impacto, confirmação clínica, confirmar
 * aplicação). Só apresentação — recebem dados/handlers do orquestrador.
 */
import { VERSOES } from "@caint/data";
import { useRef } from "react";
import type { ImpactoRemoto, ItemMigracaoRemoto, LoteDetalheRemoto, LoteRemoto } from "../../../lib/api.ts";
import { Modal, Tabela } from "../../ui/kit.tsx";
import {
  Banner,
  CodigoTecnico,
  ConfCard,
  DecisaoEcho,
  DecisaoLeiga,
  DiffPergunta,
  GrupoDecisao,
  Ico,
  ImpactoInline,
  ProgTrack,
  SetorTag,
  Stepper,
  TipoMudanca,
} from "./componentes.tsx";
import {
  type AcaoAvaliador,
  type Conferencia as ConferenciaSeg,
  canal,
  contar,
  identificarItem,
  nomeSetor,
  resumoNatural,
} from "./dominio.ts";

const VERSAO_PUBLICADA = VERSOES[0]?.versao ?? "v4.0";

function Cabecalho({
  eyebrow,
  icone,
  crown,
  titulo,
  onVoltar,
  acoes,
}: {
  eyebrow: string;
  icone: string;
  crown?: boolean;
  titulo: string;
  onVoltar?: () => void;
  acoes?: React.ReactNode;
}) {
  return (
    <div className="dk-page-head">
      <div>
        <span className={`wb-eyebrow${crown ? " crown" : ""}`}>
          <Ico name={icone} size={14} />
          {eyebrow}
        </span>
        <div className="dk-page-title" style={{ marginTop: 4 }}>
          {titulo}
        </div>
      </div>
      <div className="dk-actions">
        {acoes}
        {onVoltar && (
          <button type="button" className="m-btn" onClick={onVoltar}>
            <Ico name="back" size={14} /> Voltar
          </button>
        )}
      </div>
    </div>
  );
}

/** Toggle "Modo técnico" — só para a TI (admin); reexibe os códigos dos itens. */
function ToggleTecnico({ ativo, onToggle }: { ativo: boolean; onToggle: () => void }) {
  return (
    <label className="modo-tec">
      <input type="checkbox" checked={ativo} onChange={onToggle} /> Modo técnico
    </label>
  );
}

// ── Tela 1: Lista de atualizações ────────────────────────────────────────────

export function Lista({
  lotes,
  erro,
  ocupado,
  ehExecutor,
  onIniciar,
  onAbrir,
}: {
  lotes: LoteRemoto[] | null;
  erro: string | null;
  ocupado: boolean;
  ehExecutor: boolean;
  onIniciar: (f: File) => void;
  onAbrir: (id: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const aguardando = (lotes ?? []).filter(
    (l) => l.estado === "concluido" && l.versaoAlvo !== VERSAO_PUBLICADA,
  );
  return (
    <div className="dk-page wb">
      <Cabecalho
        eyebrow="Instrumento de avaliação"
        icone="people"
        titulo="Atualização do Instrumento de Avaliação"
        acoes={
          ehExecutor && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onIniciar(f);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                className="m-btn primary"
                disabled={ocupado}
                onClick={() => fileRef.current?.click()}
              >
                <Ico name="play" size={14} /> Iniciar nova atualização
              </button>
            </>
          )
        }
      />
      {erro && (
        <Banner tone="danger" icone="alert" titulo="Algo deu errado">
          {erro}
        </Banner>
      )}
      {ehExecutor && aguardando.length > 0 && (
        <Banner
          tone="warn"
          icone="bell"
          titulo={`${aguardando.length} atualização(ões) aguardando publicação pela TI`}
        >
          As avaliações já estão preservadas; as novas redações de texto entram no ar no próximo deploy.
        </Banner>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {lotes === null && <div className="dk-card">Carregando…</div>}
        {lotes?.length === 0 && (
          <div className="dk-card" style={{ color: "var(--text-muted)" }}>
            {ehExecutor
              ? "Nenhuma atualização no momento. Use “Iniciar nova atualização” quando a planilha oficial mudar."
              : "Nenhuma atualização no momento."}
          </div>
        )}
        {lotes?.map((l) => (
          <button
            key={l.id}
            type="button"
            className="dk-card wb-lista-row"
            onClick={() => onAbrir(l.id)}
            style={{ textAlign: "left", width: "100%" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              <div>
                <div style={{ fontWeight: 700 }}>{l.titulo}</div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                  Versão {l.versaoAlvo}
                </div>
              </div>
              <Stepper estado={l.estado} compact />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Card de item (avaliador) ─────────────────────────────────────────────────

function ItemRevisao({
  item,
  editavel,
  podeDecidir,
  mostrarTecnico,
  onDecidir,
  onImpacto,
}: {
  item: ItemMigracaoRemoto;
  editavel: boolean;
  podeDecidir: boolean;
  mostrarTecnico: boolean;
  onDecidir: (i: ItemMigracaoRemoto, a: AcaoAvaliador) => void;
  onImpacto: (i: ItemMigracaoRemoto) => void;
}) {
  const tom = canal(item) === "recode" ? "tom-renumerado" : "tom-texto";
  const off = item.decisao === "manter" || item.decisao === "rejeitar";
  return (
    <div className={`item-card ${tom}${off ? " dec-off" : ""}`}>
      <div className="item-head">
        <SetorTag setor={item.setor} />
        <TipoMudanca tipo={item.tipoProposto} />
        <span className="spacer" />
        {mostrarTecnico && <CodigoTecnico item={item} />}
        {item.decisao !== "pendente" && <DecisaoEcho decisao={item.decisao} />}
      </div>
      <DiffPergunta item={item} hero />
      <div className="item-foot">
        <ImpactoInline item={item} onAbrir={() => onImpacto(item)} />
        <DecisaoLeiga
          decisao={item.decisao}
          onDecidir={(a) => onDecidir(item, a)}
          disabled={!podeDecidir || !editavel}
        />
      </div>
    </div>
  );
}

function BarraAvaliador({ itens }: { itens: ItemMigracaoRemoto[] }) {
  const c = contar(itens);
  return (
    <div className="wb-actionbar">
      <div className="prog">
        <div className="prog-status">
          {c.revisados} de {c.total} perguntas revisadas
        </div>
        <ProgTrack a={c.aceitos} m={c.manter} d={c.descartar} />
        <div className="prog-legenda">
          <span>
            <span className="dot" style={{ background: "var(--success)" }} />
            {c.aceitos} aceitas
          </span>
          <span>
            <span className="dot" style={{ background: "var(--slate-500)" }} />
            {c.manter} mantidas
          </span>
          <span>
            <span className="dot" style={{ background: "var(--coral-400)" }} />
            {c.descartar} descartadas
          </span>
        </div>
      </div>
      <div className="acts">
        <span
          style={{
            fontSize: "var(--text-sm)",
            fontWeight: 600,
            color: c.pendente ? "var(--text-muted)" : "var(--success)",
          }}
        >
          {c.pendente > 0 ? `${c.pendente} a revisar` : "Tudo revisado · enviado à presidência"}
        </span>
      </div>
    </div>
  );
}

// ── Tela 2: Revisão (avaliador) ──────────────────────────────────────────────

export function Revisao({
  lote,
  itens,
  podeDecidir,
  mostrarTecnico,
  podeAlternar,
  ehTI,
  onToggleTecnico,
  onVoltar,
  onAlternar,
  onDecidir,
  onImpacto,
  erro,
}: {
  lote: LoteDetalheRemoto["lote"];
  itens: ItemMigracaoRemoto[];
  podeDecidir: boolean;
  mostrarTecnico: boolean;
  podeAlternar: boolean;
  ehTI: boolean;
  onToggleTecnico: () => void;
  onVoltar: () => void;
  onAlternar: () => void;
  onDecidir: (i: ItemMigracaoRemoto, a: AcaoAvaliador) => void;
  onImpacto: (i: ItemMigracaoRemoto) => void;
  erro: string | null;
}) {
  const editavel = lote.estado === "em_revisao" || lote.estado === "aguardando_aprovacao";
  const pendente = contar(itens).pendente;
  return (
    <div className="dk-page wb">
      <Cabecalho
        eyebrow="Revisão dos avaliadores"
        icone="people"
        titulo={lote.titulo}
        onVoltar={onVoltar}
        acoes={
          <>
            {ehTI && <ToggleTecnico ativo={mostrarTecnico} onToggle={onToggleTecnico} />}
            {podeAlternar && (
              <button type="button" className="m-btn" onClick={onAlternar}>
                <Ico name="crown" size={14} /> Ver conferência
              </button>
            )}
          </>
        }
      />
      <Stepper estado={lote.estado} />
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", marginTop: -8 }}>
        Para cada pergunta, compare o texto de hoje com o sugerido e escolha o que fazer. Em caso de dúvida,
        veja as avaliações já registradas.
      </p>
      {erro && (
        <Banner tone="danger" icone="alert" titulo="Algo deu errado">
          {erro}
        </Banner>
      )}
      {pendente === 0 && (
        <Banner tone="success" icone="check" titulo="Revisão concluída">
          Você revisou todas as perguntas. A presidência fará a conferência final e aplicará as mudanças
          aprovadas.
        </Banner>
      )}
      {itens.map((it) => (
        <ItemRevisao
          key={it.id}
          item={it}
          editavel={editavel}
          podeDecidir={podeDecidir}
          mostrarTecnico={mostrarTecnico}
          onDecidir={onDecidir}
          onImpacto={onImpacto}
        />
      ))}
      <BarraAvaliador itens={itens} />
    </div>
  );
}

// ── Tela 3: Conferência (presidência) ────────────────────────────────────────

function BarraPresidente({
  estado,
  conf,
  ocupado,
  ehTI,
  aguardando,
  onAplicar,
  onDesfazer,
  onBaixar,
}: {
  estado: string;
  conf: ConferenciaSeg;
  ocupado: boolean;
  ehTI: boolean;
  aguardando: boolean;
  onAplicar: () => void;
  onDesfazer: () => void;
  onBaixar: () => void;
}) {
  const aplicado = estado === "concluido";
  const status = aplicado
    ? "Atualização aplicada"
    : conf.bloqueios > 0
      ? `${conf.bloqueios} pendência(s) a resolver`
      : "Pronto para aplicar";
  return (
    <div className="wb-actionbar">
      <div className="prog">
        <div className="prog-status">{status}</div>
        <div className="prog-legenda">
          <span>
            <span className="dot" style={{ background: "var(--success)" }} />
            {conf.checks.filter((k) => k.estado === "ok").length} ok
          </span>
          <span>
            <span className="dot" style={{ background: "var(--warning)" }} />
            {conf.atencoes} atenção
          </span>
          <span>
            <span className="dot" style={{ background: "var(--danger)" }} />
            {conf.bloqueios} pendência
          </span>
        </div>
        {aplicado && aguardando && ehTI && (
          <span className="ti-pill">
            <Ico name="bell" size={12} /> Pronto para publicar (TI)
          </span>
        )}
      </div>
      <div className="acts">
        {aplicado ? (
          <>
            {ehTI && (
              <button type="button" className="m-btn lg" onClick={onBaixar}>
                <Ico name="shield" size={15} /> Baixar arquivo para a TI
              </button>
            )}
            <button type="button" className="m-btn lg" disabled={ocupado} onClick={onDesfazer}>
              <Ico name="undo" size={15} /> Desfazer esta atualização
            </button>
          </>
        ) : (
          estado !== "revertido" && (
            <button
              type="button"
              className="m-btn primary lg"
              disabled={!conf.podeAplicar || ocupado}
              onClick={onAplicar}
            >
              <Ico name="check" size={15} /> Aplicar as mudanças aprovadas
            </button>
          )
        )}
      </div>
    </div>
  );
}

export function Conferencia({
  lote,
  itens,
  conf,
  resultadoFrases,
  mostrarTecnico,
  ehTI,
  ocupado,
  onToggleTecnico,
  onVoltar,
  onAlternar,
  onImpacto,
  onAplicar,
  onDesfazer,
  onBaixar,
  erro,
}: {
  lote: LoteDetalheRemoto["lote"];
  itens: ItemMigracaoRemoto[];
  conf: ConferenciaSeg;
  resultadoFrases: string[] | null;
  mostrarTecnico: boolean;
  ehTI: boolean;
  ocupado: boolean;
  onToggleTecnico: () => void;
  onVoltar: () => void;
  onAlternar: () => void;
  onImpacto: (i: ItemMigracaoRemoto) => void;
  onAplicar: () => void;
  onDesfazer: () => void;
  onBaixar: () => void;
  erro: string | null;
}) {
  const resumo = resumoNatural(itens);
  const aguardando = lote.estado === "concluido" && lote.versaoAlvo !== VERSAO_PUBLICADA;
  void mostrarTecnico;
  const grupos = {
    texto: itens.filter((i) => i.decisao === "migrar_texto"),
    recode: itens.filter((i) => i.decisao === "migrar_recode"),
    manter: itens.filter((i) => i.decisao === "manter"),
    descartar: itens.filter((i) => i.decisao === "rejeitar"),
  };
  return (
    <div className="dk-page wb">
      <Cabecalho
        eyebrow="Conferência da presidência"
        icone="crown"
        crown
        titulo={lote.titulo}
        onVoltar={onVoltar}
        acoes={
          <>
            {ehTI && <ToggleTecnico ativo={mostrarTecnico} onToggle={onToggleTecnico} />}
            <button type="button" className="m-btn" onClick={onAlternar}>
              <Ico name="people" size={14} /> Revisar itens
            </button>
          </>
        }
      />
      <Stepper estado={lote.estado} />
      {erro && (
        <Banner tone="danger" icone="alert" titulo="Algo deu errado">
          {erro}
        </Banner>
      )}

      {lote.estado === "concluido" && (
        <Banner tone="success" icone="check" titulo="Mudanças aplicadas com sucesso">
          {resultadoFrases?.length
            ? resultadoFrases.join(" ")
            : "As avaliações foram preservadas e os índices recalculados."}{" "}
          As alterações de texto entrarão no ar após a publicação pela equipe de TI — você será avisado.
        </Banner>
      )}
      {lote.estado === "revertido" && (
        <Banner tone="warn" icone="undo" titulo="Atualização desfeita">
          Tudo voltou ao estado anterior a partir da cópia de segurança. Nenhuma avaliação foi perdida.
        </Banner>
      )}

      <div className="resumo-card">
        <span className="resumo-ico">
          <Ico name="info" size={20} />
        </span>
        <div>
          <div className="resumo-frase">{resumo.frase}</div>
          <div className="resumo-preserv">
            <Ico name="shield" size={15} /> {resumo.preserv}
          </div>
        </div>
      </div>

      <div className="grupos">
        <GrupoDecisao
          titulo="Redação atualizada"
          desc="mesma pergunta, texto melhorado"
          tom="texto"
          itens={grupos.texto}
          onImpacto={onImpacto}
        />
        <GrupoDecisao
          titulo="Itens renumerados"
          desc="avaliações transferidas para o item novo"
          tom="renumerado"
          itens={grupos.recode}
          onImpacto={onImpacto}
        />
        <GrupoDecisao
          titulo="Mantidos como estão"
          desc="nenhuma mudança"
          tom="manter"
          itens={grupos.manter}
          onImpacto={onImpacto}
        />
        <GrupoDecisao
          titulo="Descartados"
          desc="sugestão recusada"
          tom="descartar"
          itens={grupos.descartar}
          onImpacto={onImpacto}
        />
      </div>

      <div style={{ fontSize: "var(--text-sm)", fontWeight: 700, margin: "4px 0 8px" }}>
        Conferência de segurança
      </div>
      <ConfCard conf={conf} />

      <BarraPresidente
        estado={lote.estado}
        conf={conf}
        ocupado={ocupado}
        ehTI={ehTI}
        aguardando={aguardando}
        onAplicar={onAplicar}
        onDesfazer={onDesfazer}
        onBaixar={onBaixar}
      />
    </div>
  );
}

// ── Modais ───────────────────────────────────────────────────────────────────

export function ImpactoModal({
  item,
  dados,
  onClose,
}: { item: ItemMigracaoRemoto; dados: ImpactoRemoto; onClose: () => void }) {
  return (
    <Modal
      titulo="Avaliações e evidências já registradas"
      sub={`${item.perguntaAtual ?? "—"} · ${nomeSetor(item.setor)} — ${dados.respostas.length} avaliação(ões) e ${dados.evidencias.length} evidência(s)`}
      onClose={onClose}
    >
      <div style={{ fontSize: "var(--text-sm)", color: "var(--success)", marginBottom: 8 }}>
        Estes registros serão preservados — nunca apagados, e com cópia de segurança.
      </div>
      <Tabela
        colunas={[
          { rotulo: "Unidade", celula: (r: ImpactoRemoto["respostas"][number]) => r.unidadeNome },
          { rotulo: "Inicial", largura: "80px", celula: (r) => r.mi ?? "—" },
          { rotulo: "Final", largura: "80px", celula: (r) => r.mf ?? "—" },
          {
            rotulo: "Evidência (texto)",
            celula: (r) => <span style={{ fontSize: 12 }}>{r.evidenciaMi ?? "—"}</span>,
          },
          { rotulo: "Autor", largura: "120px", celula: (r) => r.autor ?? "—" },
        ]}
        linhas={dados.respostas}
        chave={(r) => r.avaliacaoId + (r.autor ?? "")}
        vazio="Nenhuma avaliação registrada para este item."
      />
      {dados.b2Indisponivel && (
        <Banner tone="warn" icone="alert" titulo="Imagens indisponíveis agora">
          Mostrando apenas os dados das evidências.
        </Banner>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {dados.evidencias.map((e) =>
          e.url ? (
            <a key={e.id} href={e.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
              {e.contentType.startsWith("image") ? (
                <img
                  src={e.url}
                  alt={e.id}
                  // Painel de impacto pode listar muitas evidências: baixa só o visível.
                  loading="lazy"
                  decoding="async"
                  width={84}
                  height={84}
                  style={{ width: 84, height: 84, objectFit: "cover", borderRadius: 6 }}
                />
              ) : (
                <span className="m-btn">
                  {e.momento} · {(e.tamanhoBytes / 1024).toFixed(0)} KB
                </span>
              )}
            </a>
          ) : (
            <span key={e.id} className="m-btn" style={{ opacity: 0.6 }}>
              {e.momento} (sem arquivo)
            </span>
          ),
        )}
      </div>
    </Modal>
  );
}

export function ConfirmacaoClinica({
  item,
  ocupado,
  onSim,
  onNao,
  onClose,
}: {
  item: ItemMigracaoRemoto;
  ocupado: boolean;
  onSim: () => void;
  onNao: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      titulo="Este item parece ter sido substituído"
      sub={identificarItem(item)}
      onClose={onClose}
      foot={
        <>
          <button type="button" className="m-btn" disabled={ocupado} onClick={onNao}>
            Não — é outro item, descartar
          </button>
          <button type="button" className="m-btn primary" disabled={ocupado} onClick={onSim}>
            <Ico name="check" size={14} /> Sim, transferir as avaliações
          </button>
        </>
      }
    >
      <div className="wb-clin-alerta">
        <Ico name="alert" size={18} />
        <span>
          A redação mudou bastante em relação à pergunta original — pode ser um item <b>diferente</b>, e não
          apenas uma nova redação.
        </span>
      </div>
      <DiffPergunta item={item} />
      <p style={{ fontSize: "var(--text-sm)", marginTop: 12 }}>
        As <b>{item.nRespostas ?? 0} avaliações</b> já feitas devem ser transferidas para este item novo?
      </p>
    </Modal>
  );
}

export function ConfirmarAplicacao({
  itens,
  ocupado,
  onConfirmar,
  onClose,
}: { itens: ItemMigracaoRemoto[]; ocupado: boolean; onConfirmar: () => void; onClose: () => void }) {
  const resumo = resumoNatural(itens);
  return (
    <Modal
      titulo="Aplicar as mudanças aprovadas?"
      onClose={onClose}
      foot={
        <>
          <button type="button" className="m-btn" disabled={ocupado} onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="m-btn primary" disabled={ocupado} onClick={onConfirmar}>
            <Ico name="check" size={14} /> Confirmar e aplicar
          </button>
        </>
      }
    >
      <div className="resumo-card" style={{ marginBottom: 12 }}>
        <span className="resumo-ico">
          <Ico name="info" size={20} />
        </span>
        <div>
          <div className="resumo-frase">{resumo.frase}</div>
          <div className="resumo-preserv">
            <Ico name="shield" size={15} /> {resumo.preserv}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
        <Ico name="undo" size={16} />
        <span>
          Há cópia de segurança de tudo. Se algo parecer errado, você pode <b>desfazer</b> esta atualização a
          qualquer momento antes da publicação.
        </span>
      </div>
    </Modal>
  );
}
